import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronLeft,
  ExternalLink,
  Info,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SignSheet } from "@/components/wallet/SignSheet";
import { XrpLogo } from "@/components/wallet/XrpLogo";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import { getOfframpQuotes } from "@/lib/wallet/markets.functions";
import { quoteSendFee } from "@/lib/wallet/fees";
import type { OnrampProviderId } from "@/lib/wallet/onramp-types";
import { addressFingerprint, formatAmount, truncateAddress } from "@/lib/wallet/format";
import { decodeXAddress, isValidAddress, xrpToDrops } from "@/lib/wallet/xrpl";
import {
  buildSellUrl,
  DEPOSIT_SUPPORTED,
  MAX_XRP,
  MIN_XRP,
  PRESET_XRP,
  TAG_WARNING,
} from "@/lib/wallet/offramp";

export const Route = createFileRoute("/sell")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Sell XRP for USD — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Compare XRP sell rates across Coinbase, Kraken, Gemini, Uphold and MoonPay, then send XRP from your own keys to the deposit address you choose.",
      },
      { property: "og:title", content: "Sell XRP for USD — Cobalt Wallet" },
      {
        property: "og:description",
        content:
          "Best-payout XRP off-ramp comparison with a locally signed payment to your chosen deposit address.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <SellScreen />
    </Gate>
  ),
});

function SellScreen() {
  const { address, submit, contacts } = useWallet();
  const overview = useQuery(overviewQuery(address));
  const price = useQuery(priceQuery(true));

  const [xrp, setXrp] = useState("500");
  const [destination, setDestination] = useState("");
  const [tag, setTag] = useState("");
  const [checked, setChecked] = useState<null | { valid: boolean; classic: string }>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const amount = Number(xrp) || 0;
  const available = overview.data?.spendable ?? 0;
  const xrpUsd = price.data ?? null;
  const tooLow = amount > 0 && amount < MIN_XRP;
  const tooHigh = amount > MAX_XRP;
  const overBalance = amount > available;
  const valid = amount >= MIN_XRP && amount <= MAX_XRP && !overBalance;

  // Debounce so keystrokes don't hammer the rate providers.
  const [quoteAmount, setQuoteAmount] = useState(500);
  useEffect(() => {
    const timer = setTimeout(() => setQuoteAmount(amount >= MIN_XRP ? amount : MIN_XRP), 350);
    return () => clearTimeout(timer);
  }, [amount]);

  const quotes = useQuery({
    queryKey: ["offramp", "quotes", quoteAmount],
    queryFn: () => getOfframpQuotes({ data: { xrpAmount: quoteAmount } }),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const [chosen, setChosen] = useState<OnrampProviderId | null>(null);
  const best = quotes.data?.quotes[0]?.id ?? null;
  const selected = chosen ?? best ?? "kraken";
  const activeQuote = quotes.data?.quotes.find((q) => q.id === selected) ?? null;

  const usdValue = xrpUsd && amount > 0 ? amount * xrpUsd : null;
  const serviceFee = useMemo(() => quoteSendFee({ usdValue, xrpUsd }), [usdValue, xrpUsd]);

  const verify = async () => {
    const value = destination.trim();
    if (!value) return;
    if (!(await isValidAddress(value))) {
      setChecked({ valid: false, classic: value });
      return;
    }
    const decoded = await decodeXAddress(value);
    setChecked({ valid: true, classic: decoded.address });
    if (decoded.tag !== null) setTag(String(decoded.tag));
  };

  const canReview = Boolean(valid && checked?.valid && address);

  const sendToVenue = async () => {
    setBusy(true);
    try {
      const tx: Record<string, unknown> = {
        TransactionType: "Payment",
        Account: address,
        Destination: checked!.classic,
        Amount: xrpToDrops(String(amount)),
      };
      if (tag.trim()) tx["DestinationTag"] = Number(tag.trim());
      const result = await submit(tx, { fee: serviceFee });
      if (result.succeeded) {
        toast.success(`XRP sent to ${activeQuote?.name ?? "venue"} · ${result.hash.slice(0, 10)}…`);
        setSheetOpen(false);
        setXrp("");
        void overview.refetch();
      } else {
        toast.error(`Ledger rejected: ${result.result}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Signing failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Sell XRP</h1>
      </div>

      <section className="panel space-y-4 p-4">
        <div>
          <p className="label-instrument">You sell (XRP)</p>
          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-border bg-background px-3">
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-elevated p-1.5">
              <XrpLogo className="size-full" />
            </span>
            <input
              value={xrp}
              onChange={(e) => setXrp(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="500"
              className="readout h-12 w-full bg-transparent text-[20px] outline-none placeholder:text-faint"
            />
            <span className="readout text-[12px] text-muted">XRP</span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {PRESET_XRP.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setXrp(String(preset))}
                className={`readout h-9 rounded-lg border text-[11px] ${
                  amount === preset ? "chip-active" : "border-border bg-background text-muted"
                }`}
              >
                {preset}
              </button>
            ))}
          </div>
          <div className="readout mt-2 flex items-center justify-between text-[10px]">
            <span className="text-faint">AVAILABLE</span>
            <button type="button" onClick={() => setXrp(String(available))} className="text-accent">
              {formatAmount(available, true)} XRP · MAX
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
          <div>
            <p className="label-instrument">You receive (estimate)</p>
            <p className="readout text-[15px]">
              {activeQuote?.usdOut != null ? `$${activeQuote.usdOut.toFixed(2)}` : "—"}
            </p>
          </div>
          <p className="readout text-right text-[10px] text-faint">
            {activeQuote?.spot ? `1 XRP ≈ $${activeQuote.spot.toFixed(4)}` : "rate unavailable"}
            <br />
            via {activeQuote?.name ?? "best venue"}
          </p>
        </div>

        {tooLow ? (
          <p className="readout text-[10px] text-danger">Minimum sale is {MIN_XRP} XRP.</p>
        ) : tooHigh ? (
          <p className="readout text-[10px] text-danger">
            Maximum per sale is {MAX_XRP.toLocaleString()} XRP.
          </p>
        ) : overBalance ? (
          <p className="readout rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-[10px] text-danger">
            Amount exceeds spendable balance. The XRPL reserve must stay funded.
          </p>
        ) : null}
      </section>

      <section className="panel space-y-3 p-4">
        <div>
          <p className="label-instrument">Deposit address at {activeQuote?.name ?? "your venue"}</p>
          <input
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              setChecked(null);
            }}
            onBlur={() => void verify()}
            spellCheck={false}
            placeholder="r… or X…"
            className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[12px] outline-none placeholder:text-faint focus:border-accent"
          />
          {checked ? (
            checked.valid ? (
              <p className="readout mt-1.5 text-[10px] text-success">
                Valid · fingerprint {addressFingerprint(checked.classic)} ·{" "}
                {truncateAddress(checked.classic, 6, 6)}
              </p>
            ) : (
              <p className="readout mt-1.5 text-[10px] text-danger">
                Not a valid XRPL address. Nothing will be signed.
              </p>
            )
          ) : (
            <p className="readout mt-1.5 text-[10px] text-faint">
              Copy this from the venue's XRP deposit page — never from a message or email.
            </p>
          )}
        </div>

        {contacts.length ? (
          <div className="flex flex-wrap gap-1.5">
            {contacts.slice(0, 6).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setDestination(c.address);
                  setTag(c.tag ?? "");
                  setChecked({ valid: true, classic: c.address });
                }}
                className="readout rounded-full border border-border bg-background px-2.5 py-1 text-[10px] text-muted"
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}

        <div>
          <p className="label-instrument">Destination tag</p>
          <input
            value={tag}
            onChange={(e) => setTag(e.target.value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="from your deposit page"
            className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
          />
          {!tag.trim() ? (
            <p className="readout mt-1.5 flex items-start gap-1.5 text-[10px] leading-relaxed text-danger">
              <AlertTriangle className="mt-0.5 size-3 shrink-0" /> {TAG_WARNING}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          disabled={!canReview}
          onClick={() => setSheetOpen(true)}
          className="readout h-12 w-full rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink disabled:opacity-40"
        >
          Review XRP transfer
        </button>

        <a
          href={buildSellUrl(selected, amount || MIN_XRP)}
          target="_blank"
          rel="noopener noreferrer"
          className="readout flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-accent/50 text-[11px] uppercase tracking-[0.15em] text-accent"
        >
          Open {activeQuote?.name ?? "venue"} cash-out <ExternalLink className="size-3.5" />
        </a>
      </section>

      <section className="panel p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="readout text-[11px] uppercase tracking-[0.15em] text-muted">
            Best sell payout
          </p>
          <button
            type="button"
            onClick={() => quotes.refetch()}
            className="readout flex items-center gap-1 text-[10px] text-faint transition-colors hover:text-foreground"
          >
            <RefreshCw className={`size-3 ${quotes.isFetching ? "animate-spin" : ""}`} /> refresh
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {quotes.isLoading
            ? [0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-14 animate-pulse rounded-lg border border-border bg-background"
                />
              ))
            : quotes.data?.quotes.map((quote, index) => {
                const isBest = index === 0 && quote.usdOut !== null;
                const isSelected = selected === quote.id;
                return (
                  <button
                    key={quote.id}
                    type="button"
                    onClick={() => setChosen(quote.id)}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
                      isSelected ? "border-accent bg-accent/10" : "border-border bg-background"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 truncate text-[12px]">
                        {quote.name}
                        {isBest ? (
                          <span className="readout flex items-center gap-1 rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-accent">
                            <Sparkles className="size-2.5" /> best
                          </span>
                        ) : null}
                        {DEPOSIT_SUPPORTED[quote.id] ? (
                          <span className="readout rounded-full border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-faint">
                            XRPL deposit
                          </span>
                        ) : null}
                      </p>
                      <p className="readout mt-0.5 truncate text-[10px] text-faint">{quote.feeNote}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="readout text-[13px]">
                        {quote.usdOut !== null ? `$${quote.usdOut.toFixed(2)}` : "—"}
                      </p>
                      <p className="readout text-[9px] text-faint">
                        {quote.spot ? `@ $${quote.spot.toFixed(4)}` : "rate unavailable"}
                      </p>
                    </div>
                  </button>
                );
              })}
        </div>

        {quotes.data ? (
          <p className="readout mt-2.5 text-[9px] text-faint">
            Estimated for {formatAmount(quotes.data.xrpAmount, true)} XRP · updated{" "}
            {new Date(quotes.data.fetchedAt).toLocaleTimeString()}
          </p>
        ) : null}

        <p className="readout mt-3 flex items-start gap-1.5 text-[10px] leading-relaxed text-faint">
          <Info className="mt-0.5 size-3 shrink-0" />
          <span>
            Payouts use each venue's public bid price with their published sell fees applied — final
            pricing is whatever the venue quotes when you cash out. Cobalt only signs the XRPL
            payment to the deposit address you enter; your keys never leave this device.
          </span>
        </p>
      </section>

      <SignSheet
        open={sheetOpen}
        title="Sign transfer to venue"
        checksum={checked?.valid ? "✓" : "!"}
        busy={busy}
        rows={[
          { label: "Operation", value: "Payment" },
          { label: "Amount", value: `${formatAmount(amount, true)} XRP` },
          { label: "Destination", value: checked?.classic ?? "" },
          {
            label: "Fingerprint",
            value: addressFingerprint(checked?.classic ?? ""),
            emphasis: "accent",
          },
          { label: "Tag", value: tag.trim() || "none" },
          { label: "Venue", value: activeQuote?.name ?? "—" },
          {
            label: "Estimated payout",
            value: activeQuote?.usdOut != null ? `≈ $${activeQuote.usdOut.toFixed(2)}` : "—",
          },
          {
            label: "Cobalt service fee",
            value: serviceFee.xrp > 0 ? `${serviceFee.xrp} XRP · ${serviceFee.basis}` : "Free",
            emphasis: serviceFee.xrp > 0 ? "accent" : "default",
          },
          { label: "Network", value: "XRPL mainnet" },
        ]}
        warning={!tag.trim() ? TAG_WARNING : undefined}
        onCancel={() => setSheetOpen(false)}
        onConfirm={() => void sendToVenue()}
      />
    </div>
  );
}
